import React, { useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

interface EditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  /** Barra de formatação fixa no topo; apenas o conteúdo rola (ex.: modais com muito HTML). */
  stickyToolbar?: boolean
}

const Editor: React.FC<EditorProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  stickyToolbar = false,
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== (value || '')) {
      editorRef.current.innerHTML = value || ''
    }
  }, [value])

  const syncContentToState = () => {
    onChange(editorRef.current?.innerHTML || '')
  }

  const executeCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value)
    editorRef.current?.focus()
    syncContentToState()
  }

  const setBlockTag = (tag: 'p' | 'h1' | 'h2' | 'h3') => executeCommand('formatBlock', tag)

  const addLink = () => {
    const url = window.prompt('URL do link:')
    if (!url) return
    executeCommand('createLink', url)
  }

  const openImagePicker = () => fileInputRef.current?.click()

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    try {
      const fileExtension = file.name.split('.').pop()
      const uniqueName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExtension}`
      const storagePath = `wiki-images/${uniqueName}`

      const { error } = await supabase.storage
        .from('wiki-images')
        .upload(storagePath, file, { cacheControl: '3600', upsert: false })
      if (error) throw error

      const { data: { publicUrl } } = supabase.storage
        .from('wiki-images')
        .getPublicUrl(storagePath)

      executeCommand('insertImage', publicUrl)
    } catch (error) {
      console.error('Erro no upload da imagem:', error)
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handlePaste = async (event: React.ClipboardEvent<HTMLDivElement>) => {
    const items = Array.from(event.clipboardData.items || [])
    const imageItem = items.find(item => item.type?.startsWith('image/'))
    if (imageItem) {
      event.preventDefault()
      const file = imageItem.getAsFile()
      if (file) {
        const syntheticEvent = { target: { files: [file] } } as unknown as React.ChangeEvent<HTMLInputElement>
        await handleFileChange(syntheticEvent)
      }
    } else {
      setTimeout(syncContentToState, 0)
    }
  }

  const toolbarClass = stickyToolbar
    ? 'flex flex-wrap gap-2 flex-shrink-0 px-2 py-2 border-b border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-800/95'
    : 'flex flex-wrap gap-2 mb-2'

  const editableClass = stickyToolbar
    ? 'ce-editable w-full min-h-0 flex-1 overflow-y-auto p-3 rounded-b border-x border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-900'
    : 'ce-editable w-full min-h-[200px] p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-900'

  const rootClass = stickyToolbar
    ? 'w-full flex flex-col min-h-[280px] max-h-[min(72vh,760px)] rounded-md border border-gray-200 dark:border-gray-600 overflow-hidden bg-gray-50 dark:bg-gray-900/50'
    : 'w-full'

  return (
    <div className={rootClass}>
      <style>{`
        .ce-editable:empty:before { content: attr(data-placeholder); color: #9CA3AF; }
      `}</style>

      <div className={toolbarClass}>
        <button type="button" className="px-2 py-1 border rounded" onClick={() => executeCommand('undo')} disabled={disabled}>Undo</button>
        <button type="button" className="px-2 py-1 border rounded" onClick={() => executeCommand('redo')} disabled={disabled}>Redo</button>
        <span className="w-px h-6 bg-gray-300 mx-1" />
        <button type="button" className="px-2 py-1 border rounded" onClick={() => executeCommand('bold')} disabled={disabled}>B</button>
        <button type="button" className="px-2 py-1 border rounded" onClick={() => executeCommand('italic')} disabled={disabled}><i>I</i></button>
        <button type="button" className="px-2 py-1 border rounded" onClick={() => executeCommand('underline')} disabled={disabled}><u>U</u></button>
        <button type="button" className="px-2 py-1 border rounded" onClick={() => executeCommand('strikeThrough')} disabled={disabled}><s>S</s></button>
        <span className="w-px h-6 bg-gray-300 mx-1" />
        <button type="button" className="px-2 py-1 border rounded" onClick={() => setBlockTag('p')} disabled={disabled}>P</button>
        <button type="button" className="px-2 py-1 border rounded" onClick={() => setBlockTag('h1')} disabled={disabled}>H1</button>
        <button type="button" className="px-2 py-1 border rounded" onClick={() => setBlockTag('h2')} disabled={disabled}>H2</button>
        <button type="button" className="px-2 py-1 border rounded" onClick={() => setBlockTag('h3')} disabled={disabled}>H3</button>
        <span className="w-px h-6 bg-gray-300 mx-1" />
        <button type="button" className="px-2 py-1 border rounded" onClick={() => executeCommand('insertUnorderedList')} disabled={disabled}>• Lista</button>
        <button type="button" className="px-2 py-1 border rounded" onClick={() => executeCommand('insertOrderedList')} disabled={disabled}>1. Lista</button>
        <span className="w-px h-6 bg-gray-300 mx-1" />
        <button type="button" className="px-2 py-1 border rounded" onClick={addLink} disabled={disabled}>Link</button>
        <button type="button" className="px-2 py-1 border rounded" onClick={openImagePicker} disabled={disabled}>Imagem</button>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
      </div>

      <div
        ref={editorRef}
        className={editableClass}
        contentEditable={!disabled}
        suppressContentEditableWarning
        data-placeholder={placeholder || 'Digite o conteúdo...'}
        onInput={syncContentToState}
        onPaste={handlePaste}
        style={stickyToolbar ? undefined : ({ resize: 'vertical' } as React.CSSProperties)}
      />
    </div>
  )
}

export default Editor
